/*
 * /enroll — the public landing page for an enrollment invite.
 *
 * Unauthenticated by design: the recipient has no session, and the token in `?d=`
 * IS the credential. It is mounted under the router's common routes rather than
 * UnAuthorizeRoutes, which bounces anyone who *does* have a session — an admin
 * checking their own invite link must not be redirected away from it.
 *
 * `d` decodes locally (unpadded base64url JSON), so the page can name the org and
 * the recipient, and can rule out an expired invite, with no network call at all.
 * Only "already used" needs the server, because nothing in the token records its
 * own redemption.
 *
 * This is the one place regenerating the QR client-side is correct: the server is
 * not in the loop here. It encodes the deep link, which is what the app scans.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCode, Spin } from "antd";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { fetchEnrollmentDetails } from "../../utils/apiservice";
import OrgAddressHint from "../../components/enrollment/OrgAddressHint";
import {
  countdown,
  decodeInvitePayload,
  deepLinkFor,
  expiryFromPayload,
  fmtExpiry,
} from "../../components/enrollment/enrollmentCommon";

// Store search URLs rather than invented direct links. Swap these for
// id-addressed links (and add the App Links / Universal Links association files)
// once the app dev supplies the bundle id and signing fingerprint — a wrong
// direct link is worse than a search that resolves.
const STORE_LINKS = [
  { label: "Google Play", href: "https://play.google.com/store/search?q=AuthNull%20Authenticator&c=apps" },
  { label: "App Store", href: "https://www.apple.com/app-store/" },
];

// The URL carries a live token: keep it out of referrers and out of indexes.
// These cover the SPA navigation case; the equivalent HTTP headers still belong
// on the server for the initial document response.
const useTokenPagePrivacy = () => {
  useEffect(() => {
    const added = [];
    const meta = (attrs) => {
      const el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    meta({ name: "referrer", content: "no-referrer" });
    meta({ name: "robots", content: "noindex, nofollow, noarchive" });
    return () => added.forEach((el) => el.remove());
  }, []);
};

const Shell = ({ children }) => (
  <div style={{
    minHeight: "100vh", background: AN_COLORS.bgWarm, fontFamily: AN_FONT,
    color: AN_COLORS.ink, display: "flex", alignItems: "center",
    justifyContent: "center", padding: "32px 20px",
  }}>
    <div style={{
      width: "100%", maxWidth: 420, background: "var(--an-surface)",
      border: `1px solid ${AN_COLORS.line}`, borderRadius: 14,
      boxShadow: "0 2px 10px rgba(16,22,60,.05)", padding: "28px 26px",
    }}>
      {children}
    </div>
  </div>
);

const Title = ({ children }) => (
  <h1 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: -0.3, color: AN_COLORS.ink }}>
    {children}
  </h1>
);

const Body = ({ children }) => (
  <p style={{ fontSize: 13.5, lineHeight: 1.55, color: AN_COLORS.ink2, margin: "10px 0 0" }}>
    {children}
  </p>
);

// A dead end that explains itself, with the next step spelled out.
const Problem = ({ title, children }) => (
  <Shell>
    <Title>{title}</Title>
    <Body>{children}</Body>
    <div style={{ marginTop: 20, fontSize: 12, color: AN_COLORS.muted }}>
      Ask your administrator to send a new invite.
    </div>
  </Shell>
);

const StoreRow = () => (
  <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${AN_COLORS.line2}` }}>
    <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginBottom: 9 }}>
      Don't have the app yet?
    </div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {STORE_LINKS.map((s) => (
        <a
          key={s.label}
          href={s.href}
          target="_blank"
          rel="noreferrer noopener"
          style={{
            display: "inline-flex", alignItems: "center", padding: "7px 12px",
            fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2,
            background: "var(--an-surface)", border: `1px solid ${AN_COLORS.line}`,
            borderRadius: 8, textDecoration: "none",
          }}
        >
          {s.label}
        </a>
      ))}
    </div>
  </div>
);

export default function EnrollPage() {
  useTokenPagePrivacy();
  const [params] = useSearchParams();
  const d = params.get("d") || "";

  const payload = useMemo(() => decodeInvitePayload(d), [d]);
  const expiry = useMemo(() => expiryFromPayload(payload), [payload]);

  // "used" cannot be known from the token, so it is asked for. Until the answer
  // arrives the page renders optimistically: a valid-looking invite should not
  // be gated behind a request that may never complete on a phone with bad signal.
  const [reason, setReason] = useState(null);
  const [checking, setChecking] = useState(false);
  const [remaining, setRemaining] = useState(() => (expiry ? countdown(expiry) : null));

  useEffect(() => {
    if (!payload?.tk) return;
    let live = true;
    setChecking(true);
    fetchEnrollmentDetails({
      apiBase: payload.b,
      token: payload.tk,
      orgId: payload.o,
      onSuccess: (res) => { if (live) { setChecking(false); setReason(res?.reason || "ok"); } },
      // A failed check is not a verdict — leave the page usable and let the app
      // reject the token if it really is spent.
      onFailure: ({ reason: r }) => {
        if (!live) return;
        setChecking(false);
        setReason(r === "used" || r === "expired" || r === "unknown" ? r : null);
      },
    });
    return () => { live = false; };
  }, [payload?.tk, payload?.o, payload?.b]);

  useEffect(() => {
    if (!expiry) return;
    const id = setInterval(() => setRemaining(countdown(expiry)), 60000);
    return () => clearInterval(id);
  }, [expiry]);

  if (!d) {
    return (
      <Problem title="This link is incomplete">
        The invite details are missing from the address. Links sometimes get cut short by chat and
        email clients — try opening it again from the original message.
      </Problem>
    );
  }

  if (!payload) {
    return (
      <Problem title="This link could not be read">
        The invite details are damaged. This usually means the link was truncated or altered on the
        way here.
      </Problem>
    );
  }

  // Checked locally, no request needed.
  const expiredLocally = !!expiry && expiry.getTime() <= Date.now();

  if (expiredLocally || reason === "expired") {
    return (
      <Problem title="This invite has expired">
        It was valid until {fmtExpiry(expiry)}. Expired invites cannot be reused.
      </Problem>
    );
  }

  // Distinct from expiry on purpose: "already used on another phone" and
  // "expired, ask for a new one" are different instructions, and one message for
  // both is how someone re-requests an invite they already redeemed.
  if (reason === "used") {
    return (
      <Problem title="This invite has already been used">
        A device has already been registered with it{payload.e ? ` for ${payload.e}` : ""}. Each
        invite works once. If that device wasn't yours, tell your administrator.
      </Problem>
    );
  }

  if (reason === "unknown") {
    return (
      <Problem title="This invite isn't recognised">
        The organisation that issued it no longer has a record of it. It may have been replaced by a
        newer invite.
      </Problem>
    );
  }

  const deepLink = deepLinkFor(d);

  return (
    <Shell>
      <div style={{
        ...{ fontFamily: AN_MONO, fontSize: 10, letterSpacing: 1.3, textTransform: "uppercase" },
        color: AN_COLORS.muted, marginBottom: 8,
      }}>
        {payload.n ? `${payload.n} · ` : ""}Device enrollment
      </div>

      <Title>Register your device</Title>
      <Body>
        {payload.e ? (
          <>
            This invite registers the AuthNull Authenticator for{" "}
            <strong style={{ fontWeight: 600, fontFamily: AN_MONO, fontSize: 12.5 }}>{payload.e}</strong>.
          </>
        ) : (
          "This invite registers the AuthNull Authenticator on your device."
        )}
      </Body>

      {/* A real button, not an anchor: the custom scheme is a button *target*,
          never a rendered link. An <a href="authnull://…"> would put the raw
          scheme in the status bar on hover and invites being copied out, which is
          the one thing the scheme must not be — it is inert everywhere except
          here and as QR content. */}
      <button
        type="button"
        onClick={() => { window.location.href = deepLink; }}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", width: "100%",
          marginTop: 20, padding: "12px 16px", fontFamily: AN_FONT, fontSize: 14,
          fontWeight: 500, cursor: "pointer", color: "#fff",
          background: AN_COLORS.primary, border: "none", borderRadius: 9,
        }}
      >
        Open in AuthNull Authenticator
      </button>

      {/* Without this, pressing the button on a device with no app installed
          appears to do nothing at all, with no explanation. */}
      <StoreRow />

      {/* The QR and the button need the app on this device; the address works
          from any phone that has it. */}
      <OrgAddressHint compact />

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${AN_COLORS.line2}` }}>
        <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginBottom: 12 }}>
          On a computer? Scan this with the Authenticator on your phone.
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          {/* Re-encoded locally — correct here, because the server is not in the
              loop on this page. It encodes the deep link, which is what the app
              expects to scan.
              errorLevel L on purpose: it keeps the QR version (and so the module
              count) as low as the payload allows, which is what makes it scannable
              off a screen at arm's length. The redundancy L trades away guards
              against print damage and smudging, neither of which applies to a
              display. */}
          {/* 192px, not smaller: a realistic payload (~215 chars of `d` plus the
              scheme) lands around QR version 10 — 57×57 modules — so 192px keeps
              roughly 3.4 device px per module. Below ~3 px/module phone cameras
              start failing at arm's length, which is the exact distance this is
              for. */}
          <QRCode value={deepLink} size={192} bordered={false} errorLevel="L" />
        </div>
      </div>

      <div style={{
        marginTop: 18, fontSize: 12, lineHeight: 1.5, color: AN_COLORS.muted,
        display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
      }}>
        {checking && <Spin size="small" />}
        {expiry
          ? <>Expires {fmtExpiry(expiry)}{remaining && remaining !== "expired" ? ` · ${remaining} left` : ""} · works once</>
          : "This invite works once."}
      </div>
    </Shell>
  );
}
